import React, { useState, useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, Reorder, LayoutGroup } from 'motion/react';
import { ArrowLeft, TrendingUp, TrendingDown, X, Plus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { stockService } from '@/services/api';

interface StockData {
  ticker: string;
  name: string;
  data: Array<{
    index: number;
    date: string;
    fullDate: string;
    timestamp: number;
    dateObj: Date;
    price: number;
    [key: string]: any;
  }>;
  performance: string;
  currentPrice: number;
  startPrice: number;
  endPrice: number;
  color: string;
}

interface StockComparisonProps {}

// Default stocks to show initially - these are always required
const DEFAULT_STOCKS = [
  { ticker: 'DAKT', name: 'Daktronics Inc' },
  { ticker: 'SVCO', name: 'Silvaco Group, Inc.' },
  { ticker: 'AMD', name: 'AMD Inc' },
  { ticker: 'NVDA', name: 'NVIDIA Corp' },
  { ticker: 'TSM', name: 'Taiwan Semi' },
];

// Required stocks that cannot be removed
const REQUIRED_STOCKS = ['DAKT', 'SVCO', 'AMD', 'NVDA', 'TSM'];

// Use consistently dark colors so all charts appear equally visible (all colors have similar lightness)
const COLORS = [
  '#000000', // Black
  '#1f2937', // Dark gray
  '#1e3a5f', // Dark blue
  '#1e293b', // Slate
  '#292524', // Dark brown
  '#1c1917', // Very dark gray
  '#0f172a', // Dark slate
  '#1e1b4b', // Dark indigo
  '#312e81', // Dark purple
  '#1e293b'  // Slate (reuse for 10th)
];

// Nice, easy-on-the-eyes colors for overlay mode - distinct and pleasant
const OVERLAY_COLORS = [
  '#3b82f6', // Blue
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#06b6d4', // Cyan
  '#f97316', // Orange
  '#ec4899', // Pink
  '#14b8a6', // Teal
  '#6366f1', // Indigo
];

// Transform API historical data to chart format
function transformHistoricalData(
  ticker: string,
  historicalData: Array<{
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    percent_change: number | null;
  }>
): StockData['data'] {
  if (!historicalData || historicalData.length === 0) return [];

  const data: StockData['data'] = [];
  
  historicalData.forEach((day, index) => {
    // Parse date string manually to avoid timezone issues
    // Format: "2025-11-13" -> parse as local date, not UTC
    const [year, month, dayNum] = day.date.split('-').map(Number);
    const date = new Date(year, month - 1, dayNum); // month is 0-indexed
    
    const monthIndex = date.getMonth();
    const monthLabel = monthIndex === 0 
      ? date.getFullYear().toString() 
      : date.toLocaleDateString('en-US', { month: 'short' });
    
    // Calculate percent change if not available
    let percentChange = day.percent_change ?? 0;
    if (percentChange === null || percentChange === undefined || percentChange === 0) {
      // Calculate from previous day if available
      if (index > 0) {
        const prevDay = historicalData[index - 1];
        if (prevDay.close > 0) {
          percentChange = ((day.close - prevDay.close) / prevDay.close) * 100;
        }
      }
    }
    
    // Use original date string (YYYY-MM-DD) for API calls
    const dateString = day.date;
    
    data.push({
      index,
      date: monthLabel,
      fullDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      price: day.close,
      timestamp: date.getTime(),
      dateObj: date,
      dateString: dateString, // Store original YYYY-MM-DD format for API calls
      percentChange: percentChange,
      [ticker]: day.close,
    });
  });

  return data;
}

export function StockComparisonPage({}: StockComparisonProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTicker = searchParams.get('ticker')?.toUpperCase();
  
  const [mode, setMode] = useState<'grid' | 'overlay' | 'strip'>('grid');
  const [primaryStock, setPrimaryStock] = useState<string>(initialTicker || DEFAULT_STOCKS[0].ticker);
  const [visibleStocks, setVisibleStocks] = useState<string[]>(() => {
    if (initialTicker) {
      // Include initial ticker first, then defaults that don't match
      return [initialTicker, ...DEFAULT_STOCKS.filter(s => s.ticker !== initialTicker).map(s => s.ticker)];
    }
    return DEFAULT_STOCKS.map(s => s.ticker);
  });
  const [stocksData, setStocksData] = useState<StockData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddStock, setShowAddStock] = useState(false);
  const [addingStock, setAddingStock] = useState(false);
  
  // Ref to access GridMode's setPrimaryStock function
  const gridModeRef = useRef<{ setPrimaryStock: (ticker: string) => void } | null>(null);
  
  // Track drag state to prevent onClick from firing after drag
  const dragStartPosition = useRef<{ x: number; y: number; ticker: string } | null>(null);
  const hasDragged = useRef<boolean>(false);
  
  // Handler to set primary stock based on current mode
  const handleSetPrimaryStock = (ticker: string) => {
    if (mode === 'grid') {
      // For grid mode, use the ref to call GridMode's setPrimaryStock
      gridModeRef.current?.setPrimaryStock(ticker);
    } else if (mode === 'strip') {
      // For strip mode, use the parent's setPrimaryStock
      setPrimaryStock(ticker);
    }
  };
  
  // Initialize selectedStocks - if initialTicker provided, start with it, otherwise use defaults
  const [selectedStocks, setSelectedStocks] = useState<Array<{ ticker: string; name: string }>>(() => {
    if (initialTicker) {
      // Start with initial ticker (name will be fetched), then add defaults that don't match
      return [
        { ticker: initialTicker, name: initialTicker }, // Placeholder name
        ...DEFAULT_STOCKS.filter(s => s.ticker !== initialTicker)
      ];
    }
    return DEFAULT_STOCKS;
  });
  
  // Fetch company name for initial ticker if provided
  useEffect(() => {
    if (initialTicker) {
      const fetchInitialStockName = async () => {
        try {
          const priceData = await stockService.getStockPrice(initialTicker);
          if (priceData?.companyName) {
            setSelectedStocks(prev => {
              const updated = prev.map(stock => 
                stock.ticker === initialTicker 
                  ? { ...stock, name: priceData.companyName || initialTicker }
                  : stock
              );
              return updated;
            });
          }
        } catch (err) {
          console.error(`Error fetching initial stock name for ${initialTicker}:`, err);
        }
      };
      fetchInitialStockName();
    }
  }, [initialTicker]);

  // Ensure required stocks are always present
  useEffect(() => {
    setSelectedStocks(prev => {
      const currentTickers = prev.map(s => s.ticker);
      const missingRequired = REQUIRED_STOCKS.filter(ticker => !currentTickers.includes(ticker));
      
      if (missingRequired.length > 0) {
        const requiredStocksToAdd = DEFAULT_STOCKS.filter(s => missingRequired.includes(s.ticker));
        return [...prev, ...requiredStocksToAdd];
      }
      
      return prev;
    });
    
    setVisibleStocks(prev => {
      const missingRequired = REQUIRED_STOCKS.filter(ticker => !prev.includes(ticker));
      if (missingRequired.length > 0) {
        return [...prev, ...missingRequired];
      }
      return prev;
    });
  }, []); // Only run once on mount

  // Fetch data for all selected stocks
  useEffect(() => {
    async function fetchStocksData() {
      if (selectedStocks.length === 0) {
        setStocksData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const promises = selectedStocks.map(async (stock, index) => {
          try {
            // Fetch historical prices (5 years = 1825 days)
            const historicalData = await stockService.getHistoricalPrices(stock.ticker, 1825);
            
            if (!historicalData || historicalData.length === 0) {
              console.warn(`No historical data for ${stock.ticker}`);
              return null;
            }

            // Fetch current price
            const priceData = await stockService.getStockPrice(stock.ticker);
            const currentPrice = priceData?.price || historicalData[historicalData.length - 1]?.close || 0;
            const startPrice = historicalData[0]?.close || currentPrice;
            const endPrice = currentPrice;

            // Transform data
            const transformedData = transformHistoricalData(stock.ticker, historicalData);
            
            // Calculate performance
            const performance = startPrice > 0 
              ? (((endPrice - startPrice) / startPrice) * 100).toFixed(1)
              : '0.0';

            return {
              ticker: stock.ticker,
              name: stock.name,
              data: transformedData,
              performance,
              currentPrice,
              startPrice,
              endPrice,
              color: COLORS[index % COLORS.length],
            } as StockData;
          } catch (err) {
            console.error(`Error fetching data for ${stock.ticker}:`, err);
            return null;
          }
        });

        const results = await Promise.all(promises);
        const validResults = results.filter((r): r is StockData => r !== null);
        
        setStocksData(validResults);
        
        // Update visible stocks to only include successfully loaded stocks
        setVisibleStocks(prev => prev.filter(ticker => 
          validResults.some(s => s.ticker === ticker)
        ));
        
        // Update primary stock if it's no longer available
        if (validResults.length > 0 && !validResults.some(s => s.ticker === primaryStock)) {
          setPrimaryStock(validResults[0].ticker);
        }
      } catch (err) {
        console.error('Error fetching stocks data:', err);
        setError('Failed to load stock data. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchStocksData();
  }, [selectedStocks, primaryStock]);

  const handleAddStock = async () => {
    const ticker = searchQuery.trim().toUpperCase();
    if (!ticker || selectedStocks.some(s => s.ticker === ticker)) {
      return;
    }

    setAddingStock(true);
    try {
      // Verify stock exists by fetching price
      const priceData = await stockService.getStockPrice(ticker);
      if (!priceData) {
        setError(`Could not find stock: ${ticker}`);
        return;
      }

      // Add stock
      const newStock = {
        ticker,
        name: priceData.companyName || ticker,
      };
      
      setSelectedStocks(prev => [...prev, newStock]);
      setVisibleStocks(prev => [...prev, ticker]);
      setSearchQuery('');
      setShowAddStock(false);
      setError(null);
    } catch (err) {
      console.error('Error adding stock:', err);
      setError(`Failed to add stock: ${ticker}`);
    } finally {
      setAddingStock(false);
    }
  };

  const handleRemoveStock = (ticker: string) => {
    // Prevent removing required stocks
    if (REQUIRED_STOCKS.includes(ticker)) {
      return;
    }
    setSelectedStocks(prev => prev.filter(s => s.ticker !== ticker));
    setVisibleStocks(prev => prev.filter(t => t !== ticker));
    if (primaryStock === ticker && selectedStocks.length > 1) {
      const remaining = selectedStocks.filter(s => s.ticker !== ticker);
      if (remaining.length > 0) {
        setPrimaryStock(remaining[0].ticker);
      }
    }
  };

  if (loading && stocksData.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header skeleton */}
        <div className="border-b border-gray-200">
          <div className="max-w-[1400px] mx-auto px-8 py-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 text-gray-400 hover:text-gray-700 transition-colors"
                >
                  <ArrowLeft className="size-4" />
                  <span className="text-sm">Back</span>
                </button>
                <div>
                  <div className="h-7 w-48 bg-gray-100 rounded-lg animate-pulse mb-2" />
                  <div className="h-4 w-64 bg-gray-100 rounded animate-pulse" />
                </div>
              </div>
              
              {/* Mode Selector skeleton */}
              <div className="flex gap-2 bg-gray-50 p-1 rounded-lg">
                <div className="h-9 w-32 bg-gray-200 rounded-md animate-pulse" />
                <div className="h-9 w-32 bg-gray-200 rounded-md animate-pulse" />
                <div className="h-9 w-32 bg-gray-200 rounded-md animate-pulse" />
              </div>
            </div>

            {/* Stock Selector skeleton */}
            <div className="flex items-center gap-2 flex-wrap">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-8 w-16 bg-gray-100 rounded-lg animate-pulse"
                />
              ))}
              <div className="h-8 w-24 bg-gray-100 rounded-lg animate-pulse" />
            </div>
          </div>
        </div>

        {/* Content skeleton */}
        <div className="max-w-[1400px] mx-auto px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="border border-gray-200 rounded-lg p-6"
              >
                {/* Header skeleton */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="h-6 w-20 bg-gray-100 rounded animate-pulse mb-2" />
                    <div className="h-4 w-32 bg-gray-100 rounded animate-pulse mb-3" />
                    <div className="h-8 w-24 bg-gray-100 rounded-lg animate-pulse mb-2" />
                    <div className="h-5 w-16 bg-gray-100 rounded animate-pulse" />
                  </div>
                </div>

                {/* Chart skeleton */}
                <div className="h-[240px] bg-gray-50 rounded-lg animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-gray-400 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="size-4" />
                <span className="text-sm">Back</span>
              </button>
              <div>
                <h1 className="text-2xl text-gray-900">Stock Comparison</h1>
                <p className="text-sm text-gray-400 mt-1">Compare stocks across different timeframes</p>
              </div>
            </div>
            
            {/* Mode Selector */}
            <div className="flex gap-2 bg-gray-50 p-1 rounded-lg">
              <button
                onClick={() => setMode('grid')}
                className={`px-4 py-2 rounded-md text-sm transition-all ${
                  mode === 'grid' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Compact Grid
              </button>
              <button
                onClick={() => setMode('overlay')}
                className={`px-4 py-2 rounded-md text-sm transition-all ${
                  mode === 'overlay' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Overlay Mode
              </button>
              <button
                onClick={() => setMode('strip')}
                className={`px-4 py-2 rounded-md text-sm transition-all ${
                  mode === 'strip' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Primary + Strip
              </button>
            </div>
          </div>

          {/* Stock Selector - Draggable */}
          <div className="flex items-center gap-2 flex-wrap">
            <Reorder.Group 
              as="div"
              axis="x" 
              values={selectedStocks} 
              onReorder={setSelectedStocks}
              className="flex items-center gap-2 flex-wrap"
              style={{ listStyle: 'none' }}
            >
              {selectedStocks.map((stock) => (
                <Reorder.Item
                  key={stock.ticker}
                  value={stock}
                  layoutId={`pill-${stock.ticker}`}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg text-sm cursor-grab active:cursor-grabbing hover:bg-gray-200 transition-colors"
                  drag
                  dragElastic={0.1}
                  dragTransition={{ bounceStiffness: 600, bounceDamping: 30 }}
                  whileDrag={{ 
                    scale: 1.05, 
                    zIndex: 1000,
                    boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
                    cursor: "grabbing"
                  }}
                  transition={{ 
                    layout: { 
                      duration: 0.3, 
                      ease: [0.25, 0.1, 0.25, 1],
                      type: "spring",
                      stiffness: 350,
                      damping: 30
                    }
                  }}
                  onDragStart={() => {
                    hasDragged.current = true;
                  }}
                  onDragEnd={() => {
                    // Reset after a short delay to prevent onClick from firing
                    setTimeout(() => {
                      hasDragged.current = false;
                      dragStartPosition.current = null;
                    }, 100);
                  }}
                  onPointerDown={(e: React.PointerEvent) => {
                    // Track initial mouse position and ticker
                    dragStartPosition.current = { 
                      x: e.clientX, 
                      y: e.clientY,
                      ticker: stock.ticker
                    };
                    hasDragged.current = false;
                  }}
                  onClick={(e: React.MouseEvent) => {
                    // Only handle click if it wasn't a drag operation
                    // Check if mouse moved significantly (more than 5px) or if drag was detected
                    const wasDrag = hasDragged.current || (
                      dragStartPosition.current && 
                      dragStartPosition.current.ticker === stock.ticker && (
                        Math.abs(e.clientX - dragStartPosition.current.x) > 5 ||
                        Math.abs(e.clientY - dragStartPosition.current.y) > 5
                      )
                    );
                    
                    if (!wasDrag && (mode === 'grid' || mode === 'strip')) {
                      handleSetPrimaryStock(stock.ticker);
                    }
                    // Reset drag tracking
                    dragStartPosition.current = null;
                  }}
                >
                  <span className="text-gray-900">{stock.ticker}</span>
                  {!REQUIRED_STOCKS.includes(stock.ticker) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveStock(stock.ticker);
                      }}
                      className="text-gray-400 hover:text-gray-700 transition-colors"
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </Reorder.Item>
              ))}
            </Reorder.Group>
            {selectedStocks.length < 10 && (
              <div className="relative">
                {showAddStock ? (
                  // Active search state
                  <div className="flex items-center gap-2">
                    {/* Input field */}
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddStock();
                        } else if (e.key === 'Escape') {
                          setShowAddStock(false);
                          setSearchQuery('');
                        }
                      }}
                      placeholder="Ticker symbol"
                      className="px-4 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
                      autoFocus
                    />
                    
                    {/* Add button */}
                    <button
                      onClick={handleAddStock}
                      disabled={addingStock || !searchQuery.trim()}
                      className="px-4 py-1.5 bg-gray-900 text-white text-sm rounded-full hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      {addingStock ? '...' : 'Add'}
                    </button>
                    
                    {/* Cancel button */}
                    <button
                      onClick={() => {
                        setShowAddStock(false);
                        setSearchQuery('');
                      }}
                      className="text-gray-400 hover:text-gray-700 text-sm transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  // Inactive state - "Add Stock" button
                  <button
                    onClick={() => setShowAddStock(true)}
                    className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <Plus className="size-4" />
                    Add Stock
                  </button>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        {stocksData.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 mb-4">No stocks selected</p>
            <button
              onClick={() => setShowAddStock(true)}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            >
              Add Your First Stock
            </button>
          </div>
        ) : (
          <>
            {mode === 'grid' && (
              <GridMode 
                ref={gridModeRef}
                stocksData={stocksData}
                selectedStocks={selectedStocks}
              />
            )}
            {mode === 'overlay' && (
              <OverlayMode 
                stocksData={stocksData}
                visibleStocks={visibleStocks}
                setVisibleStocks={setVisibleStocks}
              />
            )}
            {mode === 'strip' && (
              <StripMode 
                stocksData={stocksData}
                primaryStock={primaryStock}
                setPrimaryStock={setPrimaryStock}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Helper function to calculate Y-axis domain ensuring minimum is never negative
const calculateYAxisDomain = (data: Array<{ price: number }>) => {
  if (data.length === 0) return [0, 100];
  
  const prices = data.map((d) => d.price).filter((p) => typeof p === 'number' && !isNaN(p));
  if (prices.length === 0) return [0, 100];
  
  const dataMin = Math.min(...prices);
  const dataMax = Math.max(...prices);
  
  // Ensure minimum is never negative, add 5% padding
  const min = Math.max(0, dataMin - (dataMax - dataMin) * 0.05);
  const max = dataMax + (dataMax - dataMin) * 0.05;
  
  return [min, max];
};

// MODE 1: Compact Grid
const GridMode = forwardRef<{ setPrimaryStock: (ticker: string) => void }, {
  stocksData: StockData[];
  selectedStocks: Array<{ ticker: string; name: string }>;
}>(({ stocksData, selectedStocks }, ref) => {
  const [primaryStock, setPrimaryStockInternal] = useState<string | null>(null);
  
  // Expose setPrimaryStock via ref
  useImperativeHandle(ref, () => ({
    setPrimaryStock: (ticker: string) => {
      setPrimaryStockInternal(ticker);
    }
  }));
  const [timeframe, setTimeframe] = useState<'12M' | '3Y' | '5Y'>('5Y');
  const [loadingTimeframe, setLoadingTimeframe] = useState(false);
  const [primaryTimeframeData, setPrimaryTimeframeData] = useState<StockData['data'] | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<any>(null);
  const [executiveSummary, setExecutiveSummary] = useState<string | null>(null);
  const [highSignificanceThemes, setHighSignificanceThemes] = useState<Array<{ theme: string; key_evidence: string }> | null>(null);
  const [loadingStory, setLoadingStory] = useState(false);
  const [storyError, setStoryError] = useState<string | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Array<{
    title: string;
    source: string;
    url: string;
  }>>([]);
  
  // Get primary and other stocks - maintain selectedStocks order
  const primaryData = primaryStock ? stocksData.find(s => s.ticker === primaryStock) : null;
  const otherStocks = primaryStock 
    ? selectedStocks
        .filter(s => s.ticker !== primaryStock)
        .map(s => stocksData.find(sd => sd.ticker === s.ticker))
        .filter((s): s is StockData => s !== undefined)
    : stocksData.slice().sort((a, b) => {
        // Sort stocksData based on selectedStocks order when no primary
        const aIndex = selectedStocks.findIndex(s => s.ticker === a.ticker);
        const bIndex = selectedStocks.findIndex(s => s.ticker === b.ticker);
        return aIndex - bIndex;
      });

  // Clear selected point when primary stock or timeframe changes
  useEffect(() => {
    setSelectedPoint(null);
    setExecutiveSummary(null);
    setHighSignificanceThemes(null);
    setStoryError(null);
    setRelatedArticles([]);
  }, [primaryStock, timeframe]);

  // Fetch data when timeframe changes for primary stock
  useEffect(() => {
    if (!primaryData || !primaryStock) {
      setPrimaryTimeframeData(null);
      return;
    }

    // If timeframe is 5Y, use existing data
    if (timeframe === '5Y') {
      setPrimaryTimeframeData(primaryData.data);
      return;
    }

    const fetchTimeframeData = async () => {
      setLoadingTimeframe(true);
      try {
        let daysNeeded = timeframe === '12M' ? 365 : 1095; // 3Y = 1095 days
        const historicalData = await stockService.getHistoricalPrices(primaryStock, daysNeeded);
        
        if (historicalData && historicalData.length > 0) {
          const transformedData = transformHistoricalData(primaryStock, historicalData);
          setPrimaryTimeframeData(transformedData);
        } else {
          // Fallback to filtered existing data
          setPrimaryTimeframeData(primaryData.data);
        }
      } catch (err) {
        console.error(`Error fetching ${timeframe} data:`, err);
        // Fallback to existing data
        setPrimaryTimeframeData(primaryData.data);
      } finally {
        setLoadingTimeframe(false);
      }
    };

    fetchTimeframeData();
  }, [primaryStock, timeframe, primaryData]);

  // Calculate significant dates (largest move per month) for primary chart
  const calculateSignificantDates = (data: StockData['data']): Set<string> => {
    const significantDates = new Set<string>();
    
    if (!data || data.length === 0) return significantDates;

    // Group data by month
    const pricesByMonth = new Map<string, Array<{ dateString: string; percentChange: number }>>();
    
    data.forEach((point, index) => {
      if (index === 0) return; // Skip first point (no previous price to compare)
      
      const date = point.dateObj;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      // Calculate percent change from previous day
      const prevPrice = data[index - 1]?.price || point.price;
      const percentChange = prevPrice > 0 ? Math.abs(((point.price - prevPrice) / prevPrice) * 100) : 0;
      
      if (!pricesByMonth.has(monthKey)) {
        pricesByMonth.set(monthKey, []);
      }
      
      pricesByMonth.get(monthKey)!.push({
        dateString: point.dateString,
        percentChange
      });
    });
    
    // Find largest move in each month
    pricesByMonth.forEach((prices) => {
      if (prices.length > 0) {
        const largestMove = prices.reduce((max, current) => 
          current.percentChange > max.percentChange ? current : max
        );
        // Use dateString to match with the data points
        significantDates.add(largestMove.dateString);
      }
    });
    
    return significantDates;
  };

  // Get data to display for primary chart
  const displayData = primaryTimeframeData || primaryData?.data || [];
  const significantDates = calculateSignificantDates(displayData);

  // Calculate month ticks - sample dates to show on x-axis
  const getMonthTicks = () => {
    const ticks: string[] = [];
    const seen = new Set<string>();
    
    displayData.forEach((point) => {
      if (!point.dateObj || !point.dateString) return;
      
      const date = point.dateObj;
      const monthIndex = date.getMonth();
      const year = date.getFullYear();
      
      if (timeframe === '5Y') {
        // For 5Y: Show only January of each year
        if (monthIndex === 0) {
          const key = `${year}`;
          if (!seen.has(key)) {
            seen.add(key);
            ticks.push(point.dateString);
          }
        }
      } else if (timeframe === '3Y') {
        // For 3Y: Show every 3 months
        if (monthIndex % 3 === 0) {
          const key = `${year}-${monthIndex}`;
          if (!seen.has(key)) {
            seen.add(key);
            ticks.push(point.dateString);
          }
        }
      } else {
        // For 12M: Show first day of each month
        const key = `${year}-${monthIndex}`;
        if (!seen.has(key)) {
          seen.add(key);
          ticks.push(point.dateString);
        }
      }
    });
    
    return ticks;
  };

  // Format tick labels based on timeframe
  const formatTickLabel = (dateString: string) => {
    try {
      // Parse the YYYY-MM-DD format
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const monthIndex = date.getMonth();
      
      if (timeframe === '5Y') {
        // For 5Y: Show only years
        return date.getFullYear().toString();
      } else if (timeframe === '3Y') {
        // For 3Y: Show year for January, otherwise month
        if (monthIndex === 0) {
          return date.getFullYear().toString();
        }
        return date.toLocaleDateString('en-US', { month: 'short' });
      } else {
        // For 12M: Show year for January, otherwise month
        if (monthIndex === 0) {
          return date.getFullYear().toString();
        }
        return date.toLocaleDateString('en-US', { month: 'short' });
      }
    } catch (error) {
      return dateString;
    }
  };

  const monthTicks = getMonthTicks();

  // Handle dot click - fetch Stock_Story data and display executive_summary
  const handleDotClick = async (payload: any) => {
    if (!primaryData) return;
    
    setSelectedPoint(payload);
    setExecutiveSummary(null);
    setHighSignificanceThemes(null);
    setStoryError(null);
    setRelatedArticles([]);
    setLoadingStory(true);
    
    // Get the date in YYYY-MM-DD format
    let dateString = payload.dateString;
    if (!dateString && payload.dateObj) {
      // Fallback: construct from dateObj if dateString not available
      const date = payload.dateObj;
      const year = date.getFullYear();
      const monthNum = String(date.getMonth() + 1).padStart(2, '0');
      const dayNum = String(date.getDate()).padStart(2, '0');
      dateString = `${year}-${monthNum}-${dayNum}`;
    }
    
    if (!dateString) {
      console.warn('No date found in payload. Available keys:', Object.keys(payload));
      setStoryError('No date information available for this point');
      setLoadingStory(false);
      return;
    }
    
    try {
      console.log(`Fetching Stock Story for ${primaryData.ticker} on ${dateString}...`);
      const storyData = await stockService.getStockStory(primaryData.ticker, dateString);
      console.log('Stock Story response:', storyData);
      
      if (storyData) {
        if (storyData.executive_summary) {
          console.log('Executive summary found, length:', storyData.executive_summary.length);
          setExecutiveSummary(storyData.executive_summary);
          setHighSignificanceThemes(storyData.high_significance_themes ?? null);
          setStoryError(null);
          
          // Update selectedPoint with move_header if available
          if (storyData.move_header) {
            setSelectedPoint((prev: any) => ({
              ...prev,
              explanationTitle: storyData.move_header
            }));
          }
          
          // Fetch Stock Article Snapshot for related articles
          console.log(`Fetching Stock Article Snapshot for ${primaryData.ticker} on ${dateString}...`);
          try {
            const articleSnapshotData = await stockService.getStockArticleSnapshot(primaryData.ticker, dateString);
            console.log('Stock Article Snapshot response:', articleSnapshotData);
            
            if (articleSnapshotData && articleSnapshotData.selected_articles) {
              // Get top 3 articles
              const topArticles = [...articleSnapshotData.selected_articles]
                .sort((a, b) => {
                  if (a.relevance_score !== undefined && b.relevance_score !== undefined) {
                    return (b.relevance_score || 0) - (a.relevance_score || 0);
                  }
                  if (a.relevance_score !== undefined) return -1;
                  if (b.relevance_score !== undefined) return 1;
                  return 0;
                })
                .slice(0, 3)
                .map(article => ({
                  title: article.title,
                  source: article.source_name,
                  url: article.news_url
                }));
              
              console.log('Top 3 related articles:', topArticles);
              setRelatedArticles(topArticles);
            }
          } catch (articleError) {
            console.warn('Error fetching Stock Article Snapshot:', articleError);
            // Don't set error state, just skip related articles
          }
        } else if (storyData.error && storyData.error.includes('not found')) {
          setStoryError('Analysis not yet available. Processing may have just started. Please wait a moment and try again.');
          setExecutiveSummary(null);
        } else if (storyData.error) {
          console.warn('Stock Story API returned error:', storyData.error);
          setStoryError(storyData.error);
          setExecutiveSummary(null);
        } else {
          console.warn('No executive_summary in response:', storyData);
          setStoryError('No analysis available for this date');
          setExecutiveSummary(null);
        }
      } else {
        setStoryError('Failed to fetch analysis');
        setExecutiveSummary(null);
      }
    } catch (error) {
      console.error('Error fetching Stock Story:', error);
      setStoryError('Error loading analysis');
      setExecutiveSummary(null);
    } finally {
      setLoadingStory(false);
    }
  };

  // Custom dot component for significant moves
  const SignificantDot = (props: any) => {
    const { cx, cy, payload } = props;
    const [isHovered, setIsHovered] = useState(false);
    const [isClicked, setIsClicked] = useState(false);
    
    // Use dateString to match significant dates (more reliable than fullDate)
    if (!payload.dateString || !significantDates.has(payload.dateString)) {
      return null;
    }
    
    // Check if this dot is the currently selected one
    const isSelected = selectedPoint?.dateString === payload.dateString;
    
    const handleClick = async (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsClicked(true);
      // Trigger bounce animation
      setTimeout(() => setIsClicked(false), 600);
      // Call the parent handleDotClick function
      await handleDotClick(payload);
    };
    
    const baseRadius = 4;
    const hoverRadius = 6;
    const selectedRadius = 6;
    
    return (
      <g
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
      >
        {/* Larger invisible hit area for easier clicking */}
        <circle
          cx={cx}
          cy={cy}
          r={15}
          fill="transparent"
          strokeWidth={0}
          pointerEvents="all"
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
        />
        
        {/* Pulsing ring for selected dot */}
        {isSelected && (
          <motion.circle
            cx={cx}
            cy={cy}
            r={6}
            fill="none"
            stroke="#000000"
            strokeWidth={1.5}
            strokeOpacity={0}
            animate={{
              r: [6, 16],
              strokeOpacity: [0.4, 0],
            }}
            transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: "easeOut",
              repeatDelay: 0.2
            }}
            style={{ pointerEvents: 'none' }}
          />
        )}
        
        {/* Visible black dot that enlarges on hover and bounces on click */}
        <motion.g
          animate={{
            scale: isClicked ? [1, 1.5, 1] : 1,
          }}
          transition={{
            scale: {
              duration: isClicked ? 0.6 : 0.3,
              times: isClicked ? [0, 0.3, 1] : undefined,
              ease: isClicked ? "easeOut" : "easeInOut"
            }
          }}
          style={{ 
            transformOrigin: 'center',
            pointerEvents: 'none'
          }}
        >
          <circle
            cx={cx}
            cy={cy}
            r={isSelected ? selectedRadius : (isHovered ? hoverRadius : baseRadius)}
            fill="#000000"
            strokeWidth={0}
            style={{ 
              transition: 'r 0.2s ease-in-out',
              pointerEvents: 'none'
            }}
          />
        </motion.g>
      </g>
    );
  };

  return (
    <LayoutGroup>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="space-y-6"
      >
      {/* Primary Chart (if selected) */}
      <AnimatePresence mode="wait">
        {primaryData && (
          <motion.div
            key={`primary-${primaryData.ticker}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="border border-gray-200 rounded-lg p-6 transition-all"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl text-gray-900">{primaryData.ticker}</h2>
                  <span className="text-sm text-gray-400">{primaryData.name}</span>
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-3xl text-gray-900">${primaryData.endPrice.toFixed(2)}</span>
                  <div className={`flex items-center gap-1 ${
                    parseFloat(primaryData.performance) > 0 ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {parseFloat(primaryData.performance) > 0 ? <TrendingUp className="size-5" /> : <TrendingDown className="size-5" />}
                    <span className="text-lg">{primaryData.performance}%</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* Timeframe Toggle */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTimeframe('12M');
                    }}
                    disabled={loadingTimeframe}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      timeframe === '12M'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    } ${loadingTimeframe ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    12M
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTimeframe('3Y');
                    }}
                    disabled={loadingTimeframe}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      timeframe === '3Y'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    } ${loadingTimeframe ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    3Y
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTimeframe('5Y');
                    }}
                    disabled={loadingTimeframe}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      timeframe === '5Y'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    } ${loadingTimeframe ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    5Y
                  </button>
                </div>
                <button
                  onClick={() => setPrimaryStockInternal(null)}
                  className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  aria-label="Collapse"
                >
                  <X className="size-5" />
                </button>
              </div>
            </div>

            {displayData.length > 0 ? (
              <div style={{ width: '100%', height: '420px', minHeight: '420px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={displayData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    {timeframe === '12M' ? (
                      <XAxis 
                        dataKey="dateString" 
                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        ticks={monthTicks}
                        tickFormatter={formatTickLabel}
                      />
                    ) : (
                      <XAxis 
                        dataKey="timestamp" 
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickFormatter={(timestamp) => {
                          const date = new Date(timestamp);
                          return date.getFullYear().toString();
                        }}
                        ticks={(() => {
                          const years = new Set<number>();
                          const ticks: number[] = [];
                          let isFirstYear = true;
                          
                          displayData.forEach((point) => {
                            const year = point.dateObj.getFullYear();
                            if (!years.has(year)) {
                              years.add(year);
                              if (!isFirstYear) {
                                ticks.push(point.timestamp);
                              }
                              isFirstYear = false;
                            }
                          });
                          
                          return ticks;
                        })()}
                      />
                    )}
                    <YAxis 
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => {
                        const numValue = typeof value === 'number' ? value : parseFloat(value);
                        if (isNaN(numValue) || !isFinite(numValue)) return '';
                        if (numValue < 0 || numValue > 1000000) return '';
                        return `$${numValue.toFixed(2)}`;
                      }}
                      domain={calculateYAxisDomain(displayData)}
                      type="number"
                    />
                    <Tooltip 
                      content={({ active, payload }: any) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200">
                              <p className="text-xs text-gray-500 mb-1">{payload[0].payload.fullDate}</p>
                              <p className="text-sm text-gray-700 font-medium">${payload[0].value.toFixed(2)}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                      cursor={{ stroke: '#d1d5db', strokeWidth: 1 }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke="#000000" 
                      strokeWidth={1.2}
                      dot={<SignificantDot />}
                      activeDot={{ r: 0, fill: 'transparent', strokeWidth: 0 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[420px] flex items-center justify-center text-gray-400">
                No data available
              </div>
            )}
            
            {/* Event Explanation Panel */}
            {selectedPoint && (
              <motion.div
                layout
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200"
              >
                <motion.div layout="position">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      {loadingStory ? (
                        <div className="animate-pulse mb-1">
                          <div className="h-3 bg-gray-200 rounded w-24"></div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 mb-1">{selectedPoint.fullDate}</p>
                      )}
                      {loadingStory ? (
                        <div className="animate-pulse">
                          <div className="h-6 bg-gray-200 rounded w-48"></div>
                        </div>
                      ) : (
                        <h4 className="text-gray-700 text-lg font-medium">
                          {selectedPoint.explanationTitle || 'Significant Price Move'}
                        </h4>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setSelectedPoint(null);
                        setExecutiveSummary(null);
                        setHighSignificanceThemes(null);
                        setStoryError(null);
                        setRelatedArticles([]);
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    {loadingStory ? (
                      <>
                        <div className="animate-pulse">
                          <div className="h-6 bg-gray-200 rounded-lg w-16"></div>
                        </div>
                        <div className="animate-pulse">
                          <div className="h-5 bg-gray-200 rounded w-12"></div>
                        </div>
                        <div className="animate-pulse">
                          <div className="h-5 bg-gray-200 rounded w-20"></div>
                        </div>
                      </>
                    ) : (
                      <>
                        {selectedPoint.percentChange !== undefined && (
                          <>
                            <span className={`px-3 py-1 rounded-lg text-sm ${
                              (selectedPoint.percentChange > 0)
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-rose-100 text-rose-700'
                            }`}>
                              {selectedPoint.percentChange > 0 ? 'Positive' : 'Negative'}
                            </span>
                            <span className="text-gray-500 text-sm font-medium">
                              {selectedPoint.percentChange > 0 ? '+' : ''}{selectedPoint.percentChange?.toFixed(2)}%
                            </span>
                          </>
                        )}
                        <span className="text-gray-300 text-sm">
                          Price: ${selectedPoint.price?.toFixed(2) || 'N/A'}
                        </span>
                      </>
                    )}
                  </div>
                  
                  {loadingStory ? (
                    // Loading skeleton - same height as real content to prevent layout shift
                    <div className="space-y-2 animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-11/12"></div>
                      <div className="h-4 bg-gray-200 rounded w-10/12"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-9/12"></div>
                    </div>
                  ) : executiveSummary ? (
                    <div className="space-y-4 text-sm">
                      <p className="text-gray-600 leading-relaxed">
                        {executiveSummary}
                      </p>
                      {highSignificanceThemes && highSignificanceThemes.length > 0 && (
                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-gray-800 font-semibold mb-2">Key Reasons for the Move</p>
                          <ol className="space-y-2 list-none">
                            {highSignificanceThemes.map((item, index) => (
                              <li key={index} className="space-y-1">
                                <span className="text-gray-800 font-medium">
                                  {index + 1}. {item.theme}
                                </span>
                                <p className="text-gray-600 pl-0 leading-relaxed">{item.key_evidence}</p>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  ) : storyError ? (
                    <p className="text-gray-500 text-sm italic">
                      {storyError}
                    </p>
                  ) : (
                    <p className="text-gray-500 text-sm italic">
                      No analysis available for this date.
                    </p>
                  )}
                </motion.div>
                
                {/* Related Coverage Section */}
                <motion.div layout="position" className="mt-6 pt-5 border-t border-gray-200">
                  {loadingStory ? (
                    <div className="animate-pulse mb-3">
                      <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">Related Coverage</p>
                  )}
                  {loadingStory ? (
                    // Loading skeleton for articles - prevents layout shift
                    <div className="space-y-2.5 max-h-64 overflow-y-auto pr-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse p-2">
                          <div className="flex items-start gap-2">
                            <div className="w-4 h-4 bg-gray-200 rounded mt-0.5 flex-shrink-0"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-gray-200 rounded w-11/12"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                            </div>
                            <div className="w-3.5 h-3.5 bg-gray-200 rounded mt-1 flex-shrink-0"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : executiveSummary && relatedArticles.length > 0 ? (
                    <div className="space-y-2.5 max-h-64 overflow-y-auto overflow-x-hidden pr-2">
                      {relatedArticles.map((article, index) => (
                        <motion.a
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block group cursor-pointer hover:bg-gray-100/50 rounded-lg p-2.5 -mx-2.5 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <svg 
                              className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                              />
                            </svg>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors leading-snug line-clamp-2">
                                {article.title}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {article.source}
                              </p>
                            </div>
                            <svg 
                              className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors mt-1 flex-shrink-0" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                              />
                            </svg>
                          </div>
                        </motion.a>
                      ))}
                    </div>
                  ) : executiveSummary && !loadingStory ? (
                    <p className="text-gray-400 text-xs italic">No related articles available</p>
                  ) : null}
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grid of Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AnimatePresence mode="popLayout">
          {otherStocks.map((stock) => (
            <motion.div
              key={stock.ticker}
              layout
              layoutId={`grid-${stock.ticker}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{
                layout: {
                  duration: 0.4,
                  ease: [0.25, 0.1, 0.25, 1],
                  type: "spring",
                  stiffness: 300,
                  damping: 30
                },
                opacity: { duration: 0.2 },
                scale: { duration: 0.2 }
              }}
              className="border border-gray-200 rounded-lg p-6 hover:border-gray-400 cursor-pointer transition-all"
              onClick={() => setPrimaryStockInternal(stock.ticker)}
            >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-medium text-gray-900">{stock.ticker}</h3>
                  <span className="text-xs text-gray-400">{stock.name}</span>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-2xl text-gray-900">${stock.endPrice.toFixed(2)}</span>
                  <div className={`flex items-center gap-1 text-sm ${
                    parseFloat(stock.performance) > 0 ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {parseFloat(stock.performance) > 0 ? <TrendingUp className="size-4" /> : <TrendingDown className="size-4" />}
                    <span>{stock.performance}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Mini Chart */}
            {stock.data.length > 0 ? (
              <div style={{ width: '100%', height: '240px', minHeight: '240px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stock.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis 
                      dataKey="timestamp" 
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      tick={{ fill: '#9ca3af', fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                      tickFormatter={(timestamp) => {
                        // For 5Y view, show years
                        const date = new Date(timestamp);
                        return date.getFullYear().toString();
                      }}
                      ticks={(() => {
                        // Calculate ticks to show one per year, skipping the first year
                        const years = new Set<number>();
                        const ticks: number[] = [];
                        let isFirstYear = true;
                        
                        stock.data.forEach((point) => {
                          const year = point.dateObj.getFullYear();
                          if (!years.has(year)) {
                            years.add(year);
                            // Skip the first year label
                            if (!isFirstYear) {
                              ticks.push(point.timestamp);
                            }
                            isFirstYear = false;
                          }
                        });
                        
                        return ticks;
                      })()}
                    />
                    <YAxis 
                      tick={{ fill: '#9ca3af', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => {
                        // Ensure value is a number and format it properly
                        const numValue = typeof value === 'number' ? value : parseFloat(value);
                        if (isNaN(numValue) || !isFinite(numValue)) return '';
                        // Only format reasonable stock price values (0 to 1 million)
                        if (numValue < 0 || numValue > 1000000) return '';
                        return `$${numValue.toFixed(2)}`;
                      }}
                      domain={calculateYAxisDomain(stock.data)}
                      type="number"
                    />
                    <Tooltip 
                      content={({ active, payload }: any) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200">
                              <p className="text-xs text-gray-500 mb-1">{payload[0].payload.fullDate}</p>
                              <p className="text-sm text-gray-700 font-medium">${payload[0].value.toFixed(2)}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                      cursor={{ stroke: '#d1d5db', strokeWidth: 1 }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke="#000000" 
                      strokeWidth={1.2}
                      dot={false}
                      activeDot={{ r: 4, fill: "#000000" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[240px] flex items-center justify-center text-gray-400 text-sm">
                No data available
              </div>
            )}
          </motion.div>
        ))}
        </AnimatePresence>
      </div>
    </motion.div>
    </LayoutGroup>
  );
});

// Custom component for monthly peak dots with hover effect
const MonthlyPeakDot = ({ cx, cy, fill, isMonthlyPeak, onClick, payload, isSelected }: { 
  cx: number; 
  cy: number; 
  fill: string; 
  isMonthlyPeak: boolean;
  onClick?: (payload: any) => void;
  payload?: any;
  isSelected?: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  
  if (!isMonthlyPeak) return null;
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClicked(true);
    // Trigger bounce animation
    setTimeout(() => setIsClicked(false), 600);
    if (onClick && payload) {
      onClick(payload);
    }
  };
  
  const baseRadius = 4;
  const hoverRadius = 6;
  const selectedRadius = 6;
  
  return (
    <g
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      {/* Larger invisible hit area for easier clicking */}
      <circle
        cx={cx}
        cy={cy}
        r={15}
        fill="transparent"
        strokeWidth={0}
        pointerEvents="all"
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      {/* Pulsing ring for selected dot */}
      {isSelected && (
        <motion.circle
          cx={cx}
          cy={cy}
          r={6}
          fill="none"
          stroke={fill}
          strokeWidth={1.5}
          strokeOpacity={0}
          animate={{
            r: [6, 16],
            strokeOpacity: [0.4, 0],
          }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: "easeOut",
            repeatDelay: 0.2
          }}
          style={{ pointerEvents: 'none' }}
        />
      )}
      
      {/* Visible dot that enlarges on hover and bounces on click */}
      <motion.g
        animate={{
          scale: isClicked ? [1, 1.5, 1] : 1,
        }}
        transition={{
          scale: {
            duration: isClicked ? 0.6 : 0.3,
            times: isClicked ? [0, 0.3, 1] : undefined,
            ease: isClicked ? "easeOut" : "easeInOut"
          }
        }}
        style={{ 
          transformOrigin: 'center',
          pointerEvents: 'none'
        }}
      >
        <circle
          cx={cx}
          cy={cy}
          r={isSelected ? selectedRadius : (isHovered ? hoverRadius : baseRadius)}
          fill={fill}
          strokeWidth={0}
          style={{ 
            transition: 'r 0.2s ease-in-out',
            pointerEvents: 'none'
          }}
        />
      </motion.g>
    </g>
  );
};

// MODE 2: Overlay Mode
function OverlayMode({ stocksData, visibleStocks, setVisibleStocks }: {
  stocksData: StockData[];
  visibleStocks: string[];
  setVisibleStocks: (stocks: string[]) => void;
}) {
  const [timeframe, setTimeframe] = useState<'12M' | '3Y' | '5Y'>('5Y');
  const [loadingTimeframe, setLoadingTimeframe] = useState(false);
  const [timeframeData, setTimeframeData] = useState<Map<string, StockData['data']>>(new Map());
  const [showMonthlyPeaks, setShowMonthlyPeaks] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState<any>(null);
  const [executiveSummary, setExecutiveSummary] = useState<string | null>(null);
  const [highSignificanceThemes, setHighSignificanceThemes] = useState<Array<{ theme: string; key_evidence: string }> | null>(null);
  const [loadingStory, setLoadingStory] = useState(false);
  const [storyError, setStoryError] = useState<string | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Array<{
    title: string;
    source: string;
    url: string;
  }>>([]);
  const [selectedStockTicker, setSelectedStockTicker] = useState<string | null>(null);

  // Assign overlay colors to each stock
  const stocksWithOverlayColors = stocksData.map((stock, index) => ({
    ...stock,
    overlayColor: OVERLAY_COLORS[index % OVERLAY_COLORS.length]
  }));

  // Fetch timeframe data when timeframe changes
  useEffect(() => {
    if (stocksData.length === 0) {
      setTimeframeData(new Map());
      return;
    }

    // If timeframe is 5Y, use existing data
    if (timeframe === '5Y') {
      const dataMap = new Map<string, StockData['data']>();
      stocksData.forEach(stock => {
        dataMap.set(stock.ticker, stock.data);
      });
      setTimeframeData(dataMap);
      return;
    }

    const fetchTimeframeData = async () => {
      setLoadingTimeframe(true);
      try {
        let daysNeeded = timeframe === '12M' ? 365 : 1095; // 3Y = 1095 days
        const promises = stocksData.map(async (stock) => {
          try {
            const historicalData = await stockService.getHistoricalPrices(stock.ticker, daysNeeded);
            if (historicalData && historicalData.length > 0) {
              const transformedData = transformHistoricalData(stock.ticker, historicalData);
              return { ticker: stock.ticker, data: transformedData };
            } else {
              // Fallback to existing data
              return { ticker: stock.ticker, data: stock.data };
            }
          } catch (err) {
            console.error(`Error fetching ${timeframe} data for ${stock.ticker}:`, err);
            // Fallback to existing data
            return { ticker: stock.ticker, data: stock.data };
          }
        });

        const results = await Promise.all(promises);
        const dataMap = new Map<string, StockData['data']>();
        results.forEach(result => {
          dataMap.set(result.ticker, result.data);
        });
        setTimeframeData(dataMap);
      } catch (err) {
        console.error('Error fetching timeframe data:', err);
      } finally {
        setLoadingTimeframe(false);
      }
    };

    fetchTimeframeData();
  }, [timeframe, stocksData]);

  // Get data for each stock based on timeframe
  const getStockData = (ticker: string): StockData['data'] => {
    return timeframeData.get(ticker) || stocksData.find(s => s.ticker === ticker)?.data || [];
  };

  // Calculate monthly peak moves for each stock based on current timeframe data
  const monthlyPeaks = useMemo(() => {
    const peaks: { [ticker: string]: Set<string> } = {};
    
    stocksData.forEach((stock) => {
      peaks[stock.ticker] = new Set();
      // Get data from timeframeData map or fallback to stock.data
      const stockData = timeframeData.get(stock.ticker) || stock.data;
      if (stockData.length === 0) return;
      
      const monthlyData: { [month: string]: { maxChange: number; dateString: string; prevValue: number | null } } = {};
      
      stockData.forEach((point, index) => {
        if (index === 0) return; // Skip first point (no change to measure)
        
        // Extract month from dateString (YYYY-MM-DD format)
        const month = point.dateString ? point.dateString.substring(0, 7) : null; // YYYY-MM
        if (!month) return;
        
        const prevPrice = stockData[index - 1]?.price || stock.startPrice;
        const currentPrice = point.price;
        const absoluteChange = prevPrice > 0 ? Math.abs(((currentPrice - prevPrice) / prevPrice) * 100) : 0;
        
        if (!monthlyData[month] || absoluteChange > monthlyData[month].maxChange) {
          monthlyData[month] = {
            maxChange: absoluteChange,
            dateString: point.dateString,
            prevValue: prevPrice
          };
        }
      });
      
      // Add all peak dates to the Set
      Object.values(monthlyData).forEach(peak => {
        if (peak.dateString) {
          peaks[stock.ticker].add(peak.dateString);
        }
      });
    });
    
    return peaks;
  }, [stocksData, timeframeData]);

  // Get the base data (use first stock's data for date alignment)
  const baseData = stocksData.length > 0 ? getStockData(stocksData[0].ticker) : [];

  // Merge all stock data into single dataset for overlay
  const mergedData = baseData.length > 0
    ? baseData.map((point) => {
        const merged: any = {
          date: point.date,
          fullDate: point.fullDate,
          timestamp: point.timestamp,
          dateString: point.dateString,
          dateObj: point.dateObj,
        };
        
        stocksData.forEach((stock) => {
          const stockData = getStockData(stock.ticker);
          
          // Find the data point by matching dateString instead of using index
          // This handles cases where stocks have different listing dates
          const matchingPoint = stockData.find(d => d.dateString === point.dateString);
          
          if (matchingPoint) {
            // Normalize to percentage change from start (first available date for this stock)
            const startPrice = stockData[0]?.price || stock.startPrice;
            const currentPrice = matchingPoint.price;
            const percentChange = startPrice > 0 
              ? ((currentPrice - startPrice) / startPrice) * 100 
              : 0;
            merged[stock.ticker] = percentChange;
          } else {
            // If no data for this date, set to null so the chart doesn't render it
            merged[stock.ticker] = null;
          }
        });
        
        return merged;
      })
    : [];

  // Calculate month ticks - sample dates to show on x-axis
  const getMonthTicks = () => {
    const ticks: string[] = [];
    const seen = new Set<string>();
    
    mergedData.forEach((point) => {
      if (!point.dateObj || !point.dateString) return;
      
      const date = point.dateObj;
      const monthIndex = date.getMonth();
      const year = date.getFullYear();
      
      if (timeframe === '5Y') {
        // For 5Y: Show only January of each year
        if (monthIndex === 0) {
          const key = `${year}`;
          if (!seen.has(key)) {
            seen.add(key);
            ticks.push(point.dateString);
          }
        }
      } else if (timeframe === '3Y') {
        // For 3Y: Show only January of each year
        if (monthIndex === 0) {
          const key = `${year}`;
          if (!seen.has(key)) {
            seen.add(key);
            ticks.push(point.dateString);
          }
        }
      } else {
        // For 12M: Show first day of each month
        const key = `${year}-${monthIndex}`;
        if (!seen.has(key)) {
          seen.add(key);
          ticks.push(point.dateString);
        }
      }
    });
    
    return ticks;
  };

  // Format tick labels based on timeframe
  const formatTickLabel = (dateString: string) => {
    try {
      // Parse the YYYY-MM-DD format
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const monthIndex = date.getMonth();
      
      if (timeframe === '5Y') {
        // For 5Y: Show only years
        return date.getFullYear().toString();
      } else if (timeframe === '3Y') {
        // For 3Y: Show only years
        return date.getFullYear().toString();
      } else {
        // For 12M: Show year for January, otherwise month
        if (monthIndex === 0) {
          return date.getFullYear().toString();
        }
        return date.toLocaleDateString('en-US', { month: 'short' });
      }
    } catch (error) {
      return dateString;
    }
  };

  const monthTicks = getMonthTicks();

  // Handle dot click - fetch Stock_Story data and display executive_summary
  const handleDotClick = async (payload: any, ticker: string) => {
    // Get the stock data to find the actual price and percent change
    const stockData = stocksData.find(s => s.ticker === ticker);
    const stockDataForDate = stockData ? getStockData(ticker) : [];
    const dataPoint = stockDataForDate.find((d: any) => d.dateString === payload.dateString);
    
    // Create enhanced payload with price and percentChange
    const enhancedPayload = {
      ...payload,
      price: dataPoint?.price,
      percentChange: dataPoint?.percentChange,
      ticker: ticker
    };
    
    setSelectedPoint(enhancedPayload);
    setSelectedStockTicker(ticker);
    setExecutiveSummary(null);
    setHighSignificanceThemes(null);
    setStoryError(null);
    setRelatedArticles([]);
    setLoadingStory(true);
    
    // Get the date in YYYY-MM-DD format
    let dateString = payload.dateString;
    if (!dateString && payload.dateObj) {
      // Fallback: construct from dateObj if dateString not available
      const date = payload.dateObj;
      const year = date.getFullYear();
      const monthNum = String(date.getMonth() + 1).padStart(2, '0');
      const dayNum = String(date.getDate()).padStart(2, '0');
      dateString = `${year}-${monthNum}-${dayNum}`;
    }
    
    if (!dateString) {
      console.warn('No date found in payload. Available keys:', Object.keys(payload));
      setStoryError('No date information available for this point');
      setLoadingStory(false);
      return;
    }
    
    try {
      console.log(`Fetching Stock Story for ${ticker} on ${dateString}...`);
      const storyData = await stockService.getStockStory(ticker, dateString);
      console.log('Stock Story response:', storyData);
      
      if (storyData) {
        if (storyData.executive_summary) {
          console.log('Executive summary found, length:', storyData.executive_summary.length);
          setExecutiveSummary(storyData.executive_summary);
          setHighSignificanceThemes(storyData.high_significance_themes ?? null);
          setStoryError(null);
          
          // Update selectedPoint with move_header if available
          if (storyData.move_header) {
            setSelectedPoint((prev: any) => ({
              ...prev,
              explanationTitle: storyData.move_header
            }));
          }
          
          // Fetch Stock Article Snapshot for related articles
          console.log(`Fetching Stock Article Snapshot for ${ticker} on ${dateString}...`);
          try {
            const articleSnapshotData = await stockService.getStockArticleSnapshot(ticker, dateString);
            console.log('Stock Article Snapshot response:', articleSnapshotData);
            
            if (articleSnapshotData && articleSnapshotData.selected_articles) {
              // Get top 3 articles
              const topArticles = [...articleSnapshotData.selected_articles]
                .sort((a, b) => {
                  if (a.relevance_score !== undefined && b.relevance_score !== undefined) {
                    return (b.relevance_score || 0) - (a.relevance_score || 0);
                  }
                  if (a.relevance_score !== undefined) return -1;
                  if (b.relevance_score !== undefined) return 1;
                  return 0;
                })
                .slice(0, 3)
                .map(article => ({
                  title: article.title,
                  source: article.source_name,
                  url: article.news_url
                }));
              
              console.log('Top 3 related articles:', topArticles);
              setRelatedArticles(topArticles);
            }
          } catch (articleError) {
            console.warn('Error fetching Stock Article Snapshot:', articleError);
            // Don't set error state, just skip related articles
          }
        } else if (storyData.error && storyData.error.includes('not found')) {
          setStoryError('Analysis not yet available. Processing may have just started. Please wait a moment and try again.');
          setExecutiveSummary(null);
        } else if (storyData.error) {
          console.warn('Stock Story API returned error:', storyData.error);
          setStoryError(storyData.error);
          setExecutiveSummary(null);
        } else {
          console.warn('No executive_summary in response:', storyData);
          setStoryError('No analysis available for this date');
          setExecutiveSummary(null);
        }
      } else {
        setStoryError('Failed to fetch analysis');
        setExecutiveSummary(null);
      }
    } catch (error) {
      console.error('Error fetching Stock Story:', error);
      setStoryError('Error loading analysis');
      setExecutiveSummary(null);
    } finally {
      setLoadingStory(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="flex gap-6">
      {/* Chart */}
      <div className="flex-1 border border-gray-200 rounded-lg p-6">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h3 className="text-lg text-gray-900 mb-1">Normalized Performance</h3>
            <p className="text-sm text-gray-400">All stocks shown as % change from start of period</p>
          </div>
          {/* Timeframe Toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setTimeframe('12M')}
              disabled={loadingTimeframe}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                timeframe === '12M'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              } ${loadingTimeframe ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              12M
            </button>
            <button
              onClick={() => setTimeframe('3Y')}
              disabled={loadingTimeframe}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                timeframe === '3Y'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              } ${loadingTimeframe ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              3Y
            </button>
            <button
              onClick={() => setTimeframe('5Y')}
              disabled={loadingTimeframe}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                timeframe === '5Y'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              } ${loadingTimeframe ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              5Y
            </button>
          </div>
        </div>

        {mergedData.length > 0 ? (
          <div style={{ width: '100%', height: '520px', minHeight: '520px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mergedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                {timeframe === '12M' ? (
                  <XAxis 
                    dataKey="dateString" 
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    ticks={monthTicks}
                    tickFormatter={formatTickLabel}
                  />
                ) : (
                  <XAxis 
                    dataKey="timestamp" 
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickFormatter={(timestamp) => {
                      const date = new Date(timestamp);
                      return date.getFullYear().toString();
                    }}
                    ticks={(() => {
                      const years = new Set<number>();
                      const ticks: number[] = [];
                      let isFirstYear = true;
                      
                      mergedData.forEach((point) => {
                        const year = point.dateObj.getFullYear();
                        if (!years.has(year)) {
                          years.add(year);
                          if (!isFirstYear) {
                            ticks.push(point.timestamp);
                          }
                          isFirstYear = false;
                        }
                      });
                      
                      return ticks;
                    })()}
                  />
                )}
                <YAxis 
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value > 0 ? '+' : ''}${value}%`}
                />
                <Tooltip 
                  content={({ active, payload }: any) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200 max-h-[300px] overflow-y-auto">
                          <p className="text-xs text-gray-500 mb-2">{payload[0].payload.fullDate}</p>
                          {payload
                            .filter((p: any) => visibleStocks.includes(p.dataKey))
                            .sort((a: any, b: any) => b.value - a.value)
                            .map((p: any, i: number) => (
                              <div key={i} className="flex items-center justify-between gap-4 py-1">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-0.5 rounded" 
                                    style={{ backgroundColor: p.stroke }}
                                  />
                                  <span className="text-xs text-gray-700">{p.dataKey}</span>
                                </div>
                                <span className="text-xs font-medium text-gray-900">
                                  {p.value > 0 ? '+' : ''}{p.value.toFixed(1)}%
                                </span>
                              </div>
                            ))}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                {stocksWithOverlayColors.map((stock) => (
                  visibleStocks.includes(stock.ticker) && (
                    <Line
                      key={stock.ticker}
                      type="monotone"
                      dataKey={stock.ticker}
                      stroke={stock.overlayColor}
                      strokeWidth={1.2}
                      dot={showMonthlyPeaks ? ((props: any) => {
                        const isMonthlyPeak = monthlyPeaks[stock.ticker]?.has(props.payload.dateString);
                        const isSelected = selectedPoint?.dateString === props.payload.dateString && 
                                          selectedStockTicker === stock.ticker;
                        return (
                          <MonthlyPeakDot
                            cx={props.cx}
                            cy={props.cy}
                            fill={stock.overlayColor}
                            isMonthlyPeak={isMonthlyPeak}
                            onClick={(payload) => handleDotClick(payload, stock.ticker)}
                            payload={props.payload}
                            isSelected={isSelected}
                          />
                        );
                      }) : false}
                      activeDot={false}
                    />
                  )
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[520px] flex items-center justify-center text-gray-400">
            No data available
          </div>
        )}
      </div>

      {/* Legend/Controls */}
      <div className="w-72 border border-gray-200 rounded-lg p-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4">Toggle Stocks</h4>
        <div className="space-y-2">
          {stocksWithOverlayColors.map((stock) => (
            <label
              key={stock.ticker}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={visibleStocks.includes(stock.ticker)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setVisibleStocks([...visibleStocks, stock.ticker]);
                  } else {
                    setVisibleStocks(visibleStocks.filter((t: string) => t !== stock.ticker));
                  }
                }}
                className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
              />
              <div 
                className="w-8 h-0.5 rounded" 
                style={{ backgroundColor: stock.overlayColor }}
              />
              <div className="flex-1">
                <div className="text-sm text-gray-900">{stock.ticker}</div>
                <div className="text-xs text-gray-400">{stock.name}</div>
              </div>
              <div className={`text-xs font-medium ${
                parseFloat(stock.performance) > 0 ? 'text-gray-900' : 'text-gray-400'
              }`}>
                {parseFloat(stock.performance) > 0 ? '+' : ''}{stock.performance}%
              </div>
            </label>
          ))}
        </div>

        {/* Monthly Peaks Toggle */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={showMonthlyPeaks}
              onChange={(e) => setShowMonthlyPeaks(e.target.checked)}
              className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            />
            <div>
              <div className="text-sm font-medium text-gray-900">Show Monthly Peaks</div>
              <div className="text-xs text-gray-400">Highlight largest move each month</div>
            </div>
          </label>
        </div>
      </div>
      </div>

      {/* Event Explanation Panel */}
      {selectedPoint && (
        <motion.div
          layout
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="border border-gray-200 rounded-lg p-6 bg-gray-50"
        >
          <motion.div layout="position">
            <div className="flex items-start justify-between mb-4">
              <div>
                {loadingStory ? (
                  <div className="animate-pulse mb-1">
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 mb-1">{selectedPoint.fullDate}</p>
                )}
                {loadingStory ? (
                  <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-48"></div>
                  </div>
                ) : (
                  <h4 className="text-gray-700 text-lg font-medium">
                    {selectedPoint.explanationTitle || 'Significant Price Move'}
                    {selectedStockTicker && (
                      <span className="text-sm text-gray-500 ml-2">({selectedStockTicker})</span>
                    )}
                  </h4>
                )}
              </div>
              <button
                onClick={() => {
                  setSelectedPoint(null);
                  setExecutiveSummary(null);
                  setHighSignificanceThemes(null);
                  setStoryError(null);
                  setRelatedArticles([]);
                  setSelectedStockTicker(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-3 mb-4">
              {loadingStory ? (
                <>
                  <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded-lg w-16"></div>
                  </div>
                  <div className="animate-pulse">
                    <div className="h-5 bg-gray-200 rounded w-12"></div>
                  </div>
                  <div className="animate-pulse">
                    <div className="h-5 bg-gray-200 rounded w-20"></div>
                  </div>
                </>
              ) : (
                <>
                  {selectedPoint.percentChange !== undefined && (
                    <>
                      <span className={`px-3 py-1 rounded-lg text-sm ${
                        (selectedPoint.percentChange > 0)
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-rose-100 text-rose-700'
                      }`}>
                        {selectedPoint.percentChange > 0 ? 'Positive' : 'Negative'}
                      </span>
                      <span className="text-gray-500 text-sm font-medium">
                        {selectedPoint.percentChange > 0 ? '+' : ''}{selectedPoint.percentChange?.toFixed(2)}%
                      </span>
                    </>
                  )}
                  {selectedPoint.price !== undefined && (
                    <span className="text-gray-300 text-sm">
                      Price: ${selectedPoint.price?.toFixed(2) || 'N/A'}
                    </span>
                  )}
                </>
              )}
            </div>
            
            {loadingStory ? (
              // Loading skeleton - same height as real content to prevent layout shift
              <div className="space-y-2 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-11/12"></div>
                <div className="h-4 bg-gray-200 rounded w-10/12"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-9/12"></div>
              </div>
            ) : executiveSummary ? (
              <div className="space-y-4 text-sm">
                <p className="text-gray-600 leading-relaxed">
                  {executiveSummary}
                </p>
                {highSignificanceThemes && highSignificanceThemes.length > 0 && (
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-gray-800 font-semibold mb-2">Key Reasons for the Move</p>
                    <ol className="space-y-2 list-none">
                      {highSignificanceThemes.map((item, index) => (
                        <li key={index} className="space-y-1">
                          <span className="text-gray-800 font-medium">
                            {index + 1}. {item.theme}
                          </span>
                          <p className="text-gray-600 pl-0 leading-relaxed">{item.key_evidence}</p>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            ) : storyError ? (
              <p className="text-gray-500 text-sm italic">
                {storyError}
              </p>
            ) : (
              <p className="text-gray-500 text-sm italic">
                No analysis available for this date.
              </p>
            )}
          </motion.div>
          
          {/* Related Coverage Section */}
          <motion.div layout="position" className="mt-6 pt-5 border-t border-gray-200">
            {loadingStory ? (
              <div className="animate-pulse mb-3">
                <div className="h-3 bg-gray-200 rounded w-32"></div>
              </div>
            ) : (
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">Related Coverage</p>
            )}
            {loadingStory ? (
              // Loading skeleton for articles - prevents layout shift
              <div className="space-y-2.5 max-h-64 overflow-y-auto pr-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse p-2">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 bg-gray-200 rounded mt-0.5 flex-shrink-0"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-11/12"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                      </div>
                      <div className="w-3.5 h-3.5 bg-gray-200 rounded mt-1 flex-shrink-0"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : executiveSummary && relatedArticles.length > 0 ? (
              <div className="space-y-2.5 max-h-64 overflow-y-auto overflow-x-hidden pr-2">
                {relatedArticles.map((article, index) => (
                  <motion.a
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group cursor-pointer hover:bg-gray-100/50 rounded-lg p-2.5 -mx-2.5 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <svg 
                        className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                        />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors leading-snug line-clamp-2">
                          {article.title}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {article.source}
                        </p>
                      </div>
                      <svg 
                        className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors mt-1 flex-shrink-0" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                        />
                      </svg>
                    </div>
                  </motion.a>
                ))}
              </div>
            ) : executiveSummary && !loadingStory ? (
              <p className="text-gray-400 text-xs italic">No related articles available</p>
            ) : null}
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}

// MODE 3: Primary + Strip
function StripMode({ stocksData, primaryStock, setPrimaryStock }: {
  stocksData: StockData[];
  primaryStock: string;
  setPrimaryStock: (ticker: string) => void;
}) {
  const [timeframe, setTimeframe] = useState<'12M' | '3Y' | '5Y'>('5Y');
  const [loadingTimeframe, setLoadingTimeframe] = useState(false);
  const [primaryTimeframeData, setPrimaryTimeframeData] = useState<StockData['data'] | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<any>(null);
  const [executiveSummary, setExecutiveSummary] = useState<string | null>(null);
  const [highSignificanceThemes, setHighSignificanceThemes] = useState<Array<{ theme: string; key_evidence: string }> | null>(null);
  const [loadingStory, setLoadingStory] = useState(false);
  const [storyError, setStoryError] = useState<string | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Array<{
    title: string;
    source: string;
    url: string;
  }>>([]);
  
  const primaryData = stocksData.find(s => s.ticker === primaryStock);
  const otherStocks = stocksData.filter(s => s.ticker !== primaryStock);

  // Fetch data when timeframe changes for primary stock
  useEffect(() => {
    if (!primaryData || !primaryStock) {
      setPrimaryTimeframeData(null);
      return;
    }

    // If timeframe is 5Y, use existing data
    if (timeframe === '5Y') {
      setPrimaryTimeframeData(primaryData.data);
      return;
    }

    const fetchTimeframeData = async () => {
      setLoadingTimeframe(true);
      try {
        let daysNeeded = timeframe === '12M' ? 365 : 1095; // 3Y = 1095 days
        const historicalData = await stockService.getHistoricalPrices(primaryStock, daysNeeded);
        
        if (historicalData && historicalData.length > 0) {
          const transformedData = transformHistoricalData(primaryStock, historicalData);
          setPrimaryTimeframeData(transformedData);
        } else {
          // Fallback to filtered existing data
          setPrimaryTimeframeData(primaryData.data);
        }
      } catch (err) {
        console.error(`Error fetching ${timeframe} data:`, err);
        // Fallback to existing data
        setPrimaryTimeframeData(primaryData.data);
      } finally {
        setLoadingTimeframe(false);
      }
    };

    fetchTimeframeData();
  }, [primaryStock, timeframe, primaryData]);

  // Clear selected point when primary stock or timeframe changes
  useEffect(() => {
    setSelectedPoint(null);
    setExecutiveSummary(null);
    setHighSignificanceThemes(null);
    setStoryError(null);
    setRelatedArticles([]);
  }, [primaryStock, timeframe]);

  // Calculate significant dates (largest move per month) for primary chart
  const calculateSignificantDates = (data: StockData['data']): Set<string> => {
    const significantDates = new Set<string>();
    
    if (!data || data.length === 0) return significantDates;

    // Group data by month
    const pricesByMonth = new Map<string, Array<{ dateString: string; percentChange: number }>>();
    
    data.forEach((point, index) => {
      if (index === 0) return; // Skip first point (no previous price to compare)
      
      const date = point.dateObj;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      // Calculate percent change from previous day
      const prevPrice = data[index - 1]?.price || point.price;
      const percentChange = prevPrice > 0 ? Math.abs(((point.price - prevPrice) / prevPrice) * 100) : 0;
      
      if (!pricesByMonth.has(monthKey)) {
        pricesByMonth.set(monthKey, []);
      }
      
      pricesByMonth.get(monthKey)!.push({
        dateString: point.dateString,
        percentChange
      });
    });
    
    // Find largest move in each month
    pricesByMonth.forEach((prices) => {
      if (prices.length > 0) {
        const largestMove = prices.reduce((max, current) => 
          current.percentChange > max.percentChange ? current : max
        );
        // Use dateString to match with the data points
        significantDates.add(largestMove.dateString);
      }
    });
    
    return significantDates;
  };

  // Get data to display for primary chart
  const displayData = primaryTimeframeData || primaryData?.data || [];
  const significantDates = calculateSignificantDates(displayData);

  // Calculate month ticks - sample dates to show on x-axis
  const getMonthTicks = () => {
    const ticks: string[] = [];
    const seen = new Set<string>();
    
    displayData.forEach((point) => {
      if (!point.dateObj || !point.dateString) return;
      
      const date = point.dateObj;
      const monthIndex = date.getMonth();
      const year = date.getFullYear();
      
      if (timeframe === '5Y') {
        // For 5Y: Show only January of each year
        if (monthIndex === 0) {
          const key = `${year}`;
          if (!seen.has(key)) {
            seen.add(key);
            ticks.push(point.dateString);
          }
        }
      } else if (timeframe === '3Y') {
        // For 3Y: Show every 3 months
        if (monthIndex % 3 === 0) {
          const key = `${year}-${monthIndex}`;
          if (!seen.has(key)) {
            seen.add(key);
            ticks.push(point.dateString);
          }
        }
      } else {
        // For 12M: Show first day of each month
        const key = `${year}-${monthIndex}`;
        if (!seen.has(key)) {
          seen.add(key);
          ticks.push(point.dateString);
        }
      }
    });
    
    return ticks;
  };

  // Format tick labels based on timeframe
  const formatTickLabel = (dateString: string) => {
    try {
      // Parse the YYYY-MM-DD format
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const monthIndex = date.getMonth();
      
      if (timeframe === '5Y') {
        // For 5Y: Show only years
        return date.getFullYear().toString();
      } else if (timeframe === '3Y') {
        // For 3Y: Show year for January, otherwise month
        if (monthIndex === 0) {
          return date.getFullYear().toString();
        }
        return date.toLocaleDateString('en-US', { month: 'short' });
      } else {
        // For 12M: Show year for January, otherwise month
        if (monthIndex === 0) {
          return date.getFullYear().toString();
        }
        return date.toLocaleDateString('en-US', { month: 'short' });
      }
    } catch (error) {
      return dateString;
    }
  };

  const monthTicks = getMonthTicks();

  // Handle dot click - fetch Stock_Story data and display executive_summary
  const handleDotClick = async (payload: any) => {
    if (!primaryData) return;
    
    setSelectedPoint(payload);
    setExecutiveSummary(null);
    setHighSignificanceThemes(null);
    setStoryError(null);
    setRelatedArticles([]);
    setLoadingStory(true);
    
    // Get the date in YYYY-MM-DD format
    let dateString = payload.dateString;
    if (!dateString && payload.dateObj) {
      // Fallback: construct from dateObj if dateString not available
      const date = payload.dateObj;
      const year = date.getFullYear();
      const monthNum = String(date.getMonth() + 1).padStart(2, '0');
      const dayNum = String(date.getDate()).padStart(2, '0');
      dateString = `${year}-${monthNum}-${dayNum}`;
    }
    
    if (!dateString) {
      console.warn('No date found in payload. Available keys:', Object.keys(payload));
      setStoryError('No date information available for this point');
      setLoadingStory(false);
      return;
    }
    
    try {
      console.log(`Fetching Stock Story for ${primaryData.ticker} on ${dateString}...`);
      const storyData = await stockService.getStockStory(primaryData.ticker, dateString);
      console.log('Stock Story response:', storyData);
      
      if (storyData) {
        if (storyData.executive_summary) {
          console.log('Executive summary found, length:', storyData.executive_summary.length);
          setExecutiveSummary(storyData.executive_summary);
          setHighSignificanceThemes(storyData.high_significance_themes ?? null);
          setStoryError(null);
          
          // Update selectedPoint with move_header if available
          if (storyData.move_header) {
            setSelectedPoint((prev: any) => ({
              ...prev,
              explanationTitle: storyData.move_header
            }));
          }
          
          // Fetch Stock Article Snapshot for related articles
          console.log(`Fetching Stock Article Snapshot for ${primaryData.ticker} on ${dateString}...`);
          try {
            const articleSnapshotData = await stockService.getStockArticleSnapshot(primaryData.ticker, dateString);
            console.log('Stock Article Snapshot response:', articleSnapshotData);
            
            if (articleSnapshotData && articleSnapshotData.selected_articles) {
              // Get top 3 articles
              const topArticles = [...articleSnapshotData.selected_articles]
                .sort((a, b) => {
                  if (a.relevance_score !== undefined && b.relevance_score !== undefined) {
                    return (b.relevance_score || 0) - (a.relevance_score || 0);
                  }
                  if (a.relevance_score !== undefined) return -1;
                  if (b.relevance_score !== undefined) return 1;
                  return 0;
                })
                .slice(0, 3)
                .map(article => ({
                  title: article.title,
                  source: article.source_name,
                  url: article.news_url
                }));
              
              console.log('Top 3 related articles:', topArticles);
              setRelatedArticles(topArticles);
            }
          } catch (articleError) {
            console.warn('Error fetching Stock Article Snapshot:', articleError);
            // Don't set error state, just skip related articles
          }
        } else if (storyData.error && storyData.error.includes('not found')) {
          setStoryError('Analysis not yet available. Processing may have just started. Please wait a moment and try again.');
          setExecutiveSummary(null);
        } else if (storyData.error) {
          console.warn('Stock Story API returned error:', storyData.error);
          setStoryError(storyData.error);
          setExecutiveSummary(null);
        } else {
          console.warn('No executive_summary in response:', storyData);
          setStoryError('No analysis available for this date');
          setExecutiveSummary(null);
        }
      } else {
        setStoryError('Failed to fetch analysis');
        setExecutiveSummary(null);
      }
    } catch (error) {
      console.error('Error fetching Stock Story:', error);
      setStoryError('Error loading analysis');
      setExecutiveSummary(null);
    } finally {
      setLoadingStory(false);
    }
  };

  // Custom dot component for significant moves
  const SignificantDot = (props: any) => {
    const { cx, cy, payload } = props;
    const [isHovered, setIsHovered] = useState(false);
    const [isClicked, setIsClicked] = useState(false);
    
    // Use dateString to match significant dates (more reliable than fullDate)
    if (!payload.dateString || !significantDates.has(payload.dateString)) {
      return null;
    }
    
    // Check if this dot is the currently selected one
    const isSelected = selectedPoint?.dateString === payload.dateString;
    
    const handleClick = async (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsClicked(true);
      // Trigger bounce animation
      setTimeout(() => setIsClicked(false), 600);
      // Call the parent handleDotClick function
      await handleDotClick(payload);
    };
    
    const baseRadius = 4;
    const hoverRadius = 6;
    const selectedRadius = 6;
    
    return (
      <g
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
      >
        {/* Larger invisible hit area for easier clicking */}
        <circle
          cx={cx}
          cy={cy}
          r={15}
          fill="transparent"
          strokeWidth={0}
          pointerEvents="all"
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
        />
        
        {/* Pulsing ring for selected dot */}
        {isSelected && (
          <motion.circle
            cx={cx}
            cy={cy}
            r={6}
            fill="none"
            stroke={primaryData?.color || "#000000"}
            strokeWidth={1.5}
            strokeOpacity={0}
            animate={{
              r: [6, 16],
              strokeOpacity: [0.4, 0],
            }}
            transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: "easeOut",
              repeatDelay: 0.2
            }}
            style={{ pointerEvents: 'none' }}
          />
        )}
        
        {/* Visible dot that enlarges on hover and bounces on click */}
        <motion.g
          animate={{
            scale: isClicked ? [1, 1.5, 1] : 1,
          }}
          transition={{
            scale: {
              duration: isClicked ? 0.6 : 0.3,
              times: isClicked ? [0, 0.3, 1] : undefined,
              ease: isClicked ? "easeOut" : "easeInOut"
            }
          }}
          style={{ 
            transformOrigin: 'center',
            pointerEvents: 'none'
          }}
        >
          <circle
            cx={cx}
            cy={cy}
            r={isSelected ? selectedRadius : (isHovered ? hoverRadius : baseRadius)}
            fill={primaryData?.color || "#000000"}
            strokeWidth={0}
            style={{ 
              transition: 'r 0.2s ease-in-out',
              pointerEvents: 'none'
            }}
          />
        </motion.g>
      </g>
    );
  };

  if (!primaryData) {
    return <div className="text-center py-20 text-gray-600">No primary stock selected</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {/* Primary Chart */}
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl text-gray-900">{primaryData.ticker}</h2>
              <span className="text-sm text-gray-400">{primaryData.name}</span>
            </div>
            <div className="flex items-center gap-4 mt-2">
              <span className="text-3xl text-gray-900">${primaryData.endPrice.toFixed(2)}</span>
              <div className={`flex items-center gap-1 ${
                parseFloat(primaryData.performance) > 0 ? 'text-gray-900' : 'text-gray-400'
              }`}>
                {parseFloat(primaryData.performance) > 0 ? <TrendingUp className="size-5" /> : <TrendingDown className="size-5" />}
                <span className="text-lg">{primaryData.performance}%</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Timeframe Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setTimeframe('12M');
                }}
                disabled={loadingTimeframe}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  timeframe === '12M'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                } ${loadingTimeframe ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                12M
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setTimeframe('3Y');
                }}
                disabled={loadingTimeframe}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  timeframe === '3Y'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                } ${loadingTimeframe ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                3Y
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setTimeframe('5Y');
                }}
                disabled={loadingTimeframe}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  timeframe === '5Y'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                } ${loadingTimeframe ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                5Y
              </button>
            </div>
          </div>
        </div>

        {displayData.length > 0 ? (
          <div style={{ width: '100%', height: '520px', minHeight: '520px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={displayData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                {timeframe === '12M' ? (
                  <XAxis 
                    dataKey="dateString" 
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    ticks={monthTicks}
                    tickFormatter={formatTickLabel}
                  />
                ) : (
                  <XAxis 
                    dataKey="timestamp" 
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickFormatter={(timestamp) => {
                      const date = new Date(timestamp);
                      return date.getFullYear().toString();
                    }}
                    ticks={(() => {
                      const years = new Set<number>();
                      const ticks: number[] = [];
                      let isFirstYear = true;
                      
                      displayData.forEach((point) => {
                        const year = point.dateObj.getFullYear();
                        if (!years.has(year)) {
                          years.add(year);
                          if (!isFirstYear) {
                            ticks.push(point.timestamp);
                          }
                          isFirstYear = false;
                        }
                      });
                      
                      return ticks;
                    })()}
                  />
                )}
                <YAxis 
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => {
                    // Ensure value is a number and format it properly
                    const numValue = typeof value === 'number' ? value : parseFloat(value);
                    if (isNaN(numValue) || !isFinite(numValue)) return '';
                    // Only format reasonable stock price values (0 to 1 million)
                    if (numValue < 0 || numValue > 1000000) return '';
                    return `$${numValue.toFixed(2)}`;
                  }}
                  domain={calculateYAxisDomain(displayData)}
                  type="number"
                />
                <Tooltip 
                  content={({ active, payload }: any) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">{payload[0].payload.fullDate}</p>
                          <p className="text-sm text-gray-700 font-medium">${payload[0].value.toFixed(2)}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                  cursor={{ stroke: '#d1d5db', strokeWidth: 1 }} 
                />
                <Line 
                  type="monotone" 
                  dataKey="price" 
                  stroke={primaryData.color} 
                  strokeWidth={1.2}
                  dot={<SignificantDot />}
                  activeDot={{ r: 0, fill: 'transparent', strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[520px] flex items-center justify-center text-gray-400">
            No data available
          </div>
        )}
        
        {/* Event Explanation Panel */}
        {selectedPoint && (
          <motion.div
            layout
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200"
          >
            <motion.div layout="position">
              <div className="flex items-start justify-between mb-4">
                <div>
                  {loadingStory ? (
                    <div className="animate-pulse mb-1">
                      <div className="h-3 bg-gray-200 rounded w-24"></div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mb-1">{selectedPoint.fullDate}</p>
                  )}
                  {loadingStory ? (
                    <div className="animate-pulse">
                      <div className="h-6 bg-gray-200 rounded w-48"></div>
                    </div>
                  ) : (
                    <h4 className="text-gray-700 text-lg font-medium">
                      {selectedPoint.explanationTitle || 'Significant Price Move'}
                    </h4>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedPoint(null);
                    setExecutiveSummary(null);
                    setHighSignificanceThemes(null);
                    setStoryError(null);
                    setRelatedArticles([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex items-center gap-3 mb-4">
                {loadingStory ? (
                  <>
                    <div className="animate-pulse">
                      <div className="h-6 bg-gray-200 rounded-lg w-16"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-5 bg-gray-200 rounded w-12"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-5 bg-gray-200 rounded w-20"></div>
                    </div>
                  </>
                ) : (
                  <>
                    {selectedPoint.percentChange !== undefined && (
                      <>
                        <span className={`px-3 py-1 rounded-lg text-sm ${
                          (selectedPoint.percentChange > 0)
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-rose-100 text-rose-700'
                        }`}>
                          {selectedPoint.percentChange > 0 ? 'Positive' : 'Negative'}
                        </span>
                        <span className="text-gray-500 text-sm font-medium">
                          {selectedPoint.percentChange > 0 ? '+' : ''}{selectedPoint.percentChange?.toFixed(2)}%
                        </span>
                      </>
                    )}
                    <span className="text-gray-300 text-sm">
                      Price: ${selectedPoint.price?.toFixed(2) || 'N/A'}
                    </span>
                  </>
                )}
              </div>
              
              {loadingStory ? (
                // Loading skeleton - same height as real content to prevent layout shift
                <div className="space-y-2 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-11/12"></div>
                  <div className="h-4 bg-gray-200 rounded w-10/12"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-9/12"></div>
                </div>
              ) : executiveSummary ? (
                <div className="space-y-4 text-sm">
                  <p className="text-gray-600 leading-relaxed">
                    {executiveSummary}
                  </p>
                  {highSignificanceThemes && highSignificanceThemes.length > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-gray-800 font-semibold mb-2">Key Reasons for the Move</p>
                      <ol className="space-y-2 list-none">
                        {highSignificanceThemes.map((item, index) => (
                          <li key={index} className="space-y-1">
                            <span className="text-gray-800 font-medium">
                              {index + 1}. {item.theme}
                            </span>
                            <p className="text-gray-600 pl-0 leading-relaxed">{item.key_evidence}</p>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              ) : storyError ? (
                <p className="text-gray-500 text-sm italic">
                  {storyError}
                </p>
              ) : (
                <p className="text-gray-500 text-sm italic">
                  No analysis available for this date.
                </p>
              )}
            </motion.div>
            
            {/* Related Coverage Section */}
            <motion.div layout="position" className="mt-6 pt-5 border-t border-gray-200">
              {loadingStory ? (
                <div className="animate-pulse mb-3">
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
              ) : (
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">Related Coverage</p>
              )}
              {loadingStory ? (
                // Loading skeleton for articles - prevents layout shift
                <div className="space-y-2.5 max-h-64 overflow-y-auto pr-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse p-2">
                      <div className="flex items-start gap-2">
                        <div className="w-4 h-4 bg-gray-200 rounded mt-0.5 flex-shrink-0"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-11/12"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                        </div>
                        <div className="w-3.5 h-3.5 bg-gray-200 rounded mt-1 flex-shrink-0"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : executiveSummary && relatedArticles.length > 0 ? (
                <div className="space-y-2.5 max-h-64 overflow-y-auto overflow-x-hidden pr-2">
                  {relatedArticles.map((article, index) => (
                    <motion.a
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group cursor-pointer hover:bg-gray-100/50 rounded-lg p-2.5 -mx-2.5 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <svg 
                          className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                          />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors leading-snug line-clamp-2">
                            {article.title}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {article.source}
                          </p>
                        </div>
                        <svg 
                          className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors mt-1 flex-shrink-0" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                          />
                        </svg>
                      </div>
                    </motion.a>
                  ))}
                </div>
              ) : executiveSummary && !loadingStory ? (
                <p className="text-gray-400 text-xs italic">No related articles available</p>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Comparison Strip */}
      {otherStocks.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Compare with</h3>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {otherStocks.map((stock) => (
              <motion.button
                key={stock.ticker}
                onClick={() => setPrimaryStock(stock.ticker)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-shrink-0 w-64 border border-gray-200 rounded-lg p-4 hover:border-gray-400 transition-all cursor-pointer"
              >
                {/* Mini header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{stock.ticker}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{stock.name}</div>
                  </div>
                  <div className={`text-xs font-medium ${
                    parseFloat(stock.performance) > 0 ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {parseFloat(stock.performance) > 0 ? '+' : ''}{stock.performance}%
                  </div>
                </div>

                {/* Mini chart */}
                {stock.data.length > 0 ? (
                  <div style={{ width: '100%', height: '120px', minHeight: '120px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stock.data}>
                        <Line 
                          type="monotone" 
                          dataKey="price" 
                          stroke="#9ca3af" 
                          strokeWidth={1.2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[120px] flex items-center justify-center text-gray-300 text-xs">
                    No data
                  </div>
                )}

                <div className="text-lg text-gray-900 mt-2">${stock.endPrice.toFixed(2)}</div>
              </motion.button>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
